'use client';

import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import { ActionButtons, ResponsivePlayedMatchesList } from '@/components/molecules';
import { MatchTeamsScore } from '@/components/molecules/tie-breaker/MatchTeamsScore';
import { ResponsiveNextInPlayersList } from '@/components/molecules/tie-breaker/NextInPlayersList';
import { PlayingNowCard } from '@/components/molecules/tie-breaker/PlayingNowCard';
import { ModalWrapper } from '@/components/organisms';
import { MatchCompletedEnum, MatchStatusApiMap, MatchStatusEnum, MatchupStatusApiMap, MatchupStatusEnum } from '@/domain/constants/constants';
import { Matches } from '@/entities';
import { TieBreakerFormatType, TieBreakerScoreFormat, TieBreakerSettings, TieBreakerType } from '@/entities/Matchup';
import { SequenceStatus, TieBreakerSequence } from '@/entities/rotations/GameSequence';
import { useLastSequenceInfoQuery } from '@/hooks/tie-breaker/useLastSequnceInfo';
import { useGameSequencesMutation } from '@/hooks/tie-breaker/useRotationsMutation';
import {
	buildSequencePayloadForSave,
	getLastActiveSequence,
	getNextInLineup,
	isValidScore,
	mapPlayedSequencesToMatches,
	PlayedMatch
} from '@/infrastructure/utils/tieBreakerHelpers';
import { useTiebreakerStore } from '@/store/tie-breaker-store';

interface TieBreakerRotationCardProps {
	gameSequences: TieBreakerSequence[];
	teamOneTitle: string;
	teamTwoTitle: string;
	winBy?: number;
	tieBreakerScoreFormat: TieBreakerScoreFormat | null;
	TieBreakerSettings: TieBreakerSettings | null;
	teamOneUuid: string;
	teamTwoUuid: string;
	tieBreakerMatchId: string;
	tieBreakerMatchStatus: number;
	matchupId: string;
	tieBreakerRotatePlayerAfter: number;
	tieBreakerType?: TieBreakerType;
	matchupData?: Matches;
	isPublicMatchResultPage?: boolean;
	tieBreakerMatchCompletedType?: number;
	tieBreakerChangeToSideOutScoringPoints?: number;
	tieBreakerTitle?: string;
}

export const TieBreakerRotationCard = ({
	gameSequences,
	teamOneTitle,
	teamTwoTitle,
	tieBreakerScoreFormat,
	TieBreakerSettings,
	tieBreakerMatchStatus,
	matchupId,
	tieBreakerRotatePlayerAfter,
	isPublicMatchResultPage = false,
	tieBreakerMatchCompletedType,
	tieBreakerChangeToSideOutScoringPoints,
	tieBreakerTitle
}: TieBreakerRotationCardProps) => {
	const sequences = gameSequences;

	const localRef = useRef<HTMLDivElement>(null);
	const setTiebreakerRef = useTiebreakerStore((state) => state.setTiebreakerRef);

	const lastScoreSequence = getLastActiveSequence(sequences);

	const lastUnplayedSequence = [...sequences]
		.filter((seq) => seq.sequenceStatus === SequenceStatus.IN_PROGRESS || seq.sequenceStatus === SequenceStatus.NOT_PLAYED)
		.pop();

	const gameSequencesMatchId = sequences[0]?.matchUuid || '';

	const { data: lastSequence } = useLastSequenceInfoQuery(gameSequencesMatchId, lastUnplayedSequence?.gameNumber ?? 0);

	const [teamOneScore, setTeamOneScore] = useState<number | ''>('');
	const [teamTwoScore, setTeamTwoScore] = useState<number | ''>('');

	const [editScores, setEditScores] = useState<boolean>(false);
	const [showConfirmModal, setShowConfirmModal] = useState(false);

	const sequenceData = lastSequence?.data?.sequence;

	const teamOneNextUp = lastSequence?.data?.teamOneNextUp ?? [];
	const teamTwoNextUp = lastSequence?.data?.teamTwoNextUp ?? [];

	const isNotPlayed = sequenceData?.sequenceStatus === SequenceStatus.IN_PROGRESS || sequenceData?.sequenceStatus === SequenceStatus.NOT_PLAYED;

	const nextToPlay = isNotPlayed ? sequenceData : null;

	const currentSequenceNumber = nextToPlay?.sequenceNumber ?? 0;

	const invalidInputs = !isValidScore(String(teamOneScore)) || !isValidScore(String(teamTwoScore));
	const isEmpty = (val: unknown) => val === null || val === undefined;

	useEffect(() => {
		if (localRef.current) {
			setTiebreakerRef(localRef.current);
		}
	}, [setTiebreakerRef]);

	const { createSequence, isCreatingSequence, invalidateQueries } = useGameSequencesMutation(
		gameSequences[0]?.matchUuid || '',
		gameSequences[0]?.gameNumber.toString() || '',
		matchupId
	);

	const isSequenceInProgress = isCreatingSequence;

	const isMatchOverAndCompleted =
		tieBreakerMatchStatus === MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS] &&
		tieBreakerMatchCompletedType !== MatchCompletedEnum.WONT_BE_PLAYED_MATCH_COMPLETED_TYPE;

	useEffect(() => {
		if (lastScoreSequence && typeof lastScoreSequence.teamOneScore === 'number' && typeof lastScoreSequence.teamTwoScore === 'number') {
			setTeamOneScore(lastScoreSequence.teamOneScore);

			setTeamTwoScore(lastScoreSequence.teamTwoScore);
		}
	}, [lastScoreSequence]);

	const handleScoreChange = (e: React.ChangeEvent<HTMLInputElement>, setter: (v: number | '') => void, _tieBreakerRotatePlayerAfter?: number) => {
		const val = e.target.value;

		if (!isValidScore(val)) return;

		const num = val === '' ? '' : Number(val);

		setter(num);
	};

	const handleReEnter = () => {
		setTeamOneScore(lastScoreSequence?.teamOneScore || 0);
		setTeamTwoScore(lastScoreSequence?.teamTwoScore || 0);
		setShowConfirmModal(false);
	};

	const saveSequence = async () => {
		if (!sequenceData) return;

		const payload = buildSequencePayloadForSave({
			gameSequences,
			lastSequence: sequenceData,
			teamOneNextUp,
			teamTwoNextUp,
			isDoubles: TieBreakerSettings?.tieBreakerFormat.title === TieBreakerFormatType.DOUBLES,
			editedScores: [
				{
					uuid: sequenceData.uuid,
					teamOneScore: Number(teamOneScore),
					teamTwoScore: Number(teamTwoScore)
				}
			]
		});

		createSequence.mutate(payload, {
			onSuccess: async () => {
				await invalidateQueries();
			}
		});
	};

	const handleConfirmSaveAnyway = () => {
		setShowConfirmModal(false);
		saveSequence();
	};

	const handleSave = () => {
		const maxScore = tieBreakerScoreFormat?.game1Max;
		const scoreExceeded = maxScore !== undefined && (Number(teamOneScore) > maxScore || Number(teamTwoScore) > maxScore);

		if (scoreExceeded) {
			setShowConfirmModal(true);
			return;
		}

		saveSequence();
	};

	const updatePlayedTieBreakerSequences = (updated: PlayedMatch[]) => {
		if (!sequenceData) {
			return;
		}

		const editedScores = updated.map((match) => ({
			uuid: match.uuid,
			teamOneScore: Number(match.rightScore),
			teamTwoScore: Number(match.leftScore)
		}));

		const payload = buildSequencePayloadForSave({
			gameSequences,
			lastSequence: sequenceData,
			teamOneNextUp,
			teamTwoNextUp,
			isDoubles: TieBreakerSettings?.tieBreakerFormat.title === TieBreakerFormatType.DOUBLES,
			isEditMode: true,
			editedScores
		});

		createSequence.mutate(payload, {
			onSuccess: async () => {
				await invalidateQueries();
			}
		});
		setEditScores(false);
	};

	const createScoreHandleChange =
		(setter: (v: number | '') => void, tieBreakerRotatePlayerAfter: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
			handleScoreChange(e, setter, tieBreakerRotatePlayerAfter);
		};

	const renderSaveButton = () => {
		return (
			<>
				{tieBreakerMatchStatus !== MatchupStatusApiMap[MatchupStatusEnum.COMPLETED_MATCHUP_STATUS] && (
					<ActionButtons
						className="w-full justify-center"
						submitButtonClassName={clsx(
							'w-fit !bg-darkorange-400 !text-white !h-8 disabled:!bg-darkorange-300 disabled:opacity-50 disabled:cursor-not-allowed !gap-3 !mt-2 !py-1 !px-2 !text-xs',
							isMatchOverAndCompleted && '!p-0'
						)}
						closeButtonClassName="!w-fit !text-darkorange-600 w-full hidden"
						onClose={() => {}}
						onSubmit={handleSave}
						submitButtonText={!isMatchOverAndCompleted ? 'Save' : 'Save Completed DreamBreaker'}
						closeButtonText="Cancel"
						iconClassName="size-4"
						submitButtonDisabled={
							!isMatchOverAndCompleted && (isSequenceInProgress || invalidInputs || isEmpty(teamOneScore) || isEmpty(teamTwoScore))
						}
						isLoading={isSequenceInProgress}
					/>
				)}
				{tieBreakerChangeToSideOutScoringPoints !== 0 && tieBreakerChangeToSideOutScoringPoints && !isMatchOverAndCompleted ? (
					<span className="text-gray-600 py-1 font-normal text-sm sm:text-base">
						Players rotate after {tieBreakerChangeToSideOutScoringPoints.toString().split('').join(', ')} points
					</span>
				) : null}
			</>
		);
	};

	return (
		<div ref={localRef} className="pt-8">
			<div className="rounded-3xl border border-gray-400 py-6 px-0 sm:px-4 md:px-9 flex flex-col bg-white">
				<div className="px-4 lg:px-10 xl:px-48 gap-6">
					<p className="text-sm pb-6 font-semibold">
						{tieBreakerScoreFormat?.title} {tieBreakerTitle ? ` | ${tieBreakerTitle}` : null}
					</p>

					{isMatchOverAndCompleted ? <p className="text-lg py-4 font-semibold text-darkorange-400">{tieBreakerTitle} completed!</p> : null}

					<MatchTeamsScore
						teamOneTitle={teamOneTitle}
						teamTwoTitle={teamTwoTitle}
						teamOneTotal={lastScoreSequence?.teamOneScore || 0}
						teamTwoTotal={lastScoreSequence?.teamTwoScore || 0}
						isMatchOverAndCompleted={isMatchOverAndCompleted}
					/>
					{isMatchOverAndCompleted && !isPublicMatchResultPage ? (
						<div className="flex justify-center pt-4">{renderSaveButton()}</div>
					) : null}

					<PlayingNowCard
						nextToPlay={
							nextToPlay
								? {
										teamOnePlayerOneName: nextToPlay.teamOnePlayerOneName,
										teamOnePlayerTwoName: nextToPlay.teamOnePlayerTwoName ?? undefined,
										teamTwoPlayerOneName: nextToPlay.teamTwoPlayerOneName,
										teamTwoPlayerTwoName: nextToPlay.teamTwoPlayerTwoName ?? undefined
									}
								: null
						}
						teamOneScore={teamOneScore}
						teamTwoScore={teamTwoScore}
						setTeamOneScore={setTeamOneScore}
						setTeamTwoScore={setTeamTwoScore}
						createScoreHandleChange={createScoreHandleChange}
						tieBreakerRotatePlayerAfter={tieBreakerRotatePlayerAfter}
						isMatchOver={isMatchOverAndCompleted}
						isMatchOverAndCompleted={isMatchOverAndCompleted}
						currentSequenceNumber={currentSequenceNumber}
						renderSaveButton={renderSaveButton}
						isPublicMatchResultPage={isPublicMatchResultPage}
					/>
					<ResponsivePlayedMatchesList
						playedMatches={mapPlayedSequencesToMatches(gameSequences) as any}
						isMatchOverAndCompleted={isMatchOverAndCompleted}
						editScores={editScores}
						setEditScores={setEditScores}
						updatePlayedTieBreakerSequences={updatePlayedTieBreakerSequences}
						isEditing={editScores}
						isUpdatingSequence={isCreatingSequence}
						isPublicMatchResultPage={isPublicMatchResultPage}
					/>

					{!isMatchOverAndCompleted && mapPlayedSequencesToMatches(gameSequences) && (
						<div className="w-full py-2 border-b border-b-darkorange-400" />
					)}

					{!isMatchOverAndCompleted && (
						<ResponsiveNextInPlayersList
							nextInTeamOne={getNextInLineup(teamOneNextUp)}
							nextInTeamTwo={getNextInLineup(teamTwoNextUp)}
							isMatchOverAndCompleted={isMatchOverAndCompleted}
							teamOneTitle={teamOneTitle}
							teamTwoTitle={teamTwoTitle}
						/>
					)}
				</div>
			</div>

			{showConfirmModal ? (
				<ModalWrapper title={'Invalid score'} onClose={handleReEnter} className="max-w-[343px] gap-0 p-6 text-start">
					<p className="font-normal pt-4 pb-10 text-gray-500">
						One of the scores do not meet the score format for this game. If you have done so in error, please correct the score by
						clicking “Re-enter”. If the score is correct, click “Save anyway”
					</p>
					<div className="flex w-full justify-between gap-4">
						<button onClick={handleReEnter} className="px-4 py-2 text-sm border w-full border-gray-300 rounded-md hover:bg-gray-100">
							Re-enter
						</button>
						<button
							onClick={handleConfirmSaveAnyway}
							className="px-4 py-2 text-sm bg-darkorange-400 w-full text-white rounded-md hover:bg-darkorange-500"
						>
							Save anyway
						</button>
					</div>
				</ModalWrapper>
			) : null}
		</div>
	);
};
